/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { styles } from '../ReportsScreen.styles';
import { colors } from '../../../shared/theme/colors';
import { spacing } from '../../../shared/theme/spacing';
import { CatalogPagination } from '../../../shared/ui/CatalogPagination';
import type { ReportsScreenViewModel } from '../useReportsScreen';
import { ReportsDenseSectionShell } from './ReportsDenseSectionShell';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { OperationalFilterChipGroup } from '../../../shared/filters/OperationalFilterPrimitives';
import { PaperSheet } from '../../../shared/ticket/PaperSheet';
import { formatMoney } from '../../../shared/format/public-api';

function formatQty(n: number, uom: string | null): string {
  const unit = (uom ?? 'u').trim() || 'u';
  const rounded = Math.abs(n) >= 100 ? Math.round(n) : Math.round(n * 1000) / 1000;
  return `${rounded.toLocaleString('es-AR')} ${unit}`;
}

/**
 * Merma por producto (compra / venta / shrinkage) — Informes → Operación.
 */
export function ReportsStockMermaSection({
  vm,
  dateRangeLabel,
  periodDataLoading,
}: {
  vm: ReportsScreenViewModel;
  dateRangeLabel: string;
  periodDataLoading: boolean;
}) {
  if (!vm.canInventory) return null;

  const pageSize = vm.stockMermaPageSize;
  const hasMore = (vm.stockMermaPage + 1) * pageSize < vm.stockMermaTotalCount;
  const showRemoteHint = vm.stockMermaSource === null && !vm.stockMermaLoading;

  return (
    <ReportsDenseSectionShell accessibilityLabel={`Merma · ${dateRangeLabel}`}>
      <Text style={styles.reportsDenseBlockTitle}>Merma</Text>
      <Text style={styles.caption}>
        Cuánto compraste, vendiste y perdiste en el período. Si falta registrar algo, andá a Stock.
      </Text>

      <View style={localStyles.filterRow}>
        <OperationalFilterChipGroup
          ticketTone
          layout="hug"
          options={[
            { value: 'all', label: 'Todas', accessibilityLabel: 'Todas las unidades' },
            { value: 'kg', label: 'Por kilo', accessibilityLabel: 'Solo productos en kg o g' },
          ]}
          value={vm.stockMermaUomFilter}
          onChange={(id) => vm.setStockMermaUomFilter(id as 'all' | 'kg')}
        />
      </View>

      {vm.stockMermaLoading ? (
        <View
          style={styles.ventasPeriodUpdatingRow}
          accessibilityLiveRegion="polite"
          accessibilityLabel="Cargando merma"
        >
          <ActivityIndicator size="small" color={ticketColors.ink2} accessibilityLabel="Cargando" />
          <Text style={styles.ventasPeriodUpdatingText}>Cargando merma…</Text>
        </View>
      ) : showRemoteHint ? (
        <Text style={[styles.caption, { color: colors.warning }]}>
          Sin red. Conectate y tirale para abajo para actualizar.
        </Text>
      ) : vm.stockMermaTotalCount === 0 ? (
        <PaperSheet padded style={localStyles.emptySheet}>
          <Text style={localStyles.emptyTitle}>Nada en este período</Text>
          <Text style={styles.caption}>
            Cuando recibas compras, vendas o registres merma, acá se arma el resumen.
          </Text>
        </PaperSheet>
      ) : (
        <>
          <View style={localStyles.summaryGrid}>
            <View style={localStyles.summaryCell}>
              <Text style={localStyles.summaryLabel}>Merma</Text>
              <Text style={[localStyles.summaryValue, vm.stockMermaSumShrinkage > 0 && localStyles.summaryBad]}>
                {vm.stockMermaSumShrinkage.toLocaleString('es-AR')}
              </Text>
            </View>
            <View style={localStyles.summaryCell}>
              <Text style={localStyles.summaryLabel}>Compra</Text>
              <Text style={localStyles.summaryValue}>{vm.stockMermaSumPurchased.toLocaleString('es-AR')}</Text>
            </View>
            <View style={localStyles.summaryCell}>
              <Text style={localStyles.summaryLabel}>Venta</Text>
              <Text style={localStyles.summaryValue}>{vm.stockMermaSumSold.toLocaleString('es-AR')}</Text>
            </View>
            {vm.stockMermaSumExpiry > 0 ? (
              <View style={localStyles.summaryCell}>
                <Text style={localStyles.summaryLabel}>Vencidas</Text>
                <Text style={[localStyles.summaryValue, localStyles.summaryBad]}>
                  {vm.stockMermaSumExpiry.toLocaleString('es-AR')}
                </Text>
              </View>
            ) : null}
            {vm.stockMermaSumShrinkageCost > 0 ? (
              <View style={localStyles.summaryCell}>
                <Text style={localStyles.summaryLabel}>Costo merma</Text>
                <Text style={localStyles.summaryValue}>
                  {formatMoney(Math.round(vm.stockMermaSumShrinkageCost))}
                </Text>
              </View>
            ) : null}
          </View>

          {vm.stockMermaRows.map((row) => (
            <View key={row.productId} style={localStyles.row} testID={`informes.merma.row.${row.productId}`}>
              <Text style={localStyles.rowTitle} numberOfLines={2}>
                {row.label}
              </Text>
              <View style={localStyles.fieldRow}>
                <Text style={styles.reportsDenseListFieldLabel}>Compra</Text>
                <Text style={styles.reportsDenseListFieldValue}>
                  {formatQty(row.purchased, row.unitOfMeasure)}
                </Text>
              </View>
              <View style={localStyles.fieldRow}>
                <Text style={styles.reportsDenseListFieldLabel}>Venta</Text>
                <Text style={styles.reportsDenseListFieldValue}>
                  {formatQty(row.sold, row.unitOfMeasure)}
                </Text>
              </View>
              <View style={localStyles.fieldRow}>
                <Text style={styles.reportsDenseListFieldLabel}>Merma</Text>
                <Text
                  style={[
                    styles.reportsDenseListFieldValue,
                    row.shrinkage > 0 && { color: ticketColors.bad },
                  ]}
                >
                  {formatQty(row.shrinkage, row.unitOfMeasure)}
                </Text>
              </View>
              {row.breakage > 0 ? (
                <View style={localStyles.fieldRow}>
                  <Text style={styles.reportsDenseListFieldLabel}>Rotura</Text>
                  <Text style={styles.reportsDenseListFieldValue}>
                    {formatQty(row.breakage, row.unitOfMeasure)}
                  </Text>
                </View>
              ) : null}
              {row.expiry > 0 ? (
                <View style={localStyles.fieldRow}>
                  <Text style={styles.reportsDenseListFieldLabel}>Vencidas</Text>
                  <Text style={[styles.reportsDenseListFieldValue, { color: ticketColors.warn }]}>
                    {formatQty(row.expiry, row.unitOfMeasure)}
                  </Text>
                </View>
              ) : null}
            </View>
          ))}

          <CatalogPagination
            variant="default"
            placement="footer"
            catalogPageIndex={vm.stockMermaPage}
            pageSize={pageSize}
            catalogTotalCount={vm.stockMermaTotalCount}
            hasMore={hasMore}
            productsLoading={vm.stockMermaLoading}
            productCount={vm.stockMermaRows.length}
            listNouns={{ singular: 'producto', plural: 'productos' }}
            alwaysShowPagination={vm.stockMermaTotalCount > pageSize}
            onPreviousPage={vm.goToPrevStockMermaPage}
            onNextPage={vm.goToNextStockMermaPage}
          />
        </>
      )}

      <View style={localStyles.actions}>
        <TouchableOpacity
          style={[styles.primaryButton, localStyles.actionBtn]}
          onPress={vm.handleOpenStockToRegisterMerma}
          accessibilityLabel="Registrar merma en Stock"
          accessibilityRole="button"
          testID="informes.merma.register-cta"
        >
          <Ionicons name="remove-circle-outline" size={18} color={ticketColors.paper} />
          <Text style={styles.primaryButtonText}>Registrar merma</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.secondaryButton,
            (periodDataLoading || vm.stockMermaLoading || showRemoteHint || vm.stockMermaTotalCount === 0) &&
              styles.disabled,
            localStyles.actionBtn,
          ]}
          onPress={vm.handleShareStockMerma}
          disabled={
            periodDataLoading ||
            vm.stockMermaLoading ||
            showRemoteHint ||
            vm.stockMermaTotalCount === 0
          }
          accessibilityLabel="Exportar merma a CSV"
          accessibilityRole="button"
        >
          <Ionicons name="download-outline" size={18} color={ticketColors.ink} />
          <Text style={styles.secondaryButtonText}>Exportar CSV</Text>
        </TouchableOpacity>
      </View>
    </ReportsDenseSectionShell>
  );
}

const localStyles = StyleSheet.create({
  filterRow: {
    marginTop: spacing.sm,
    marginBottom: spacing.sm,
  },
  emptySheet: {
    marginTop: spacing.xs,
  },
  emptyTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.ink,
    marginBottom: 4,
  },
  summaryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  summaryCell: {
    minWidth: 88,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
  },
  summaryLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: ticketColors.ink3,
    marginBottom: 2,
  },
  summaryValue: {
    fontSize: 16,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  summaryBad: {
    color: ticketColors.bad,
  },
  row: {
    paddingVertical: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
    gap: 2,
  },
  rowTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.ink,
    marginBottom: 4,
  },
  fieldRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 2,
  },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    alignSelf: 'flex-start',
  },
});
